import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { logoUrl } from '../mockData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isChiSiamoOpen, setIsChiSiamoOpen] = useState(false);
  const [isMobileChiSiamoOpen, setIsMobileChiSiamoOpen] = useState(false);
  const chiSiamoRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (chiSiamoRef.current && !chiSiamoRef.current.contains(e.target)) {
        setIsChiSiamoOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
      setIsChiSiamoOpen(false);
    }
  };

  const navLink = 'text-[#1B2380] hover:text-[#0E8C84] font-medium transition-colors duration-300';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 transition-shadow duration-300 ${isScrolled ? 'shadow-md' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-[72px]">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')} data-testid="header-logo">
            <img
              src={logoUrl}
              alt="Assietica Società Cooperativa"
              className="h-16 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-7">
            {/* Chi Siamo Dropdown */}
            <div
              ref={chiSiamoRef}
              className="relative"
              onMouseEnter={() => setIsChiSiamoOpen(true)}
              onMouseLeave={() => setIsChiSiamoOpen(false)}
            >
              <button
                data-testid="nav-chi-siamo"
                onClick={() => setIsChiSiamoOpen((prev) => !prev)}
                className={`flex items-center space-x-1 ${navLink}`}
              >
                <span>Chi Siamo</span>
                <ChevronDown size={16} className={`transition-transform duration-300 ${isChiSiamoOpen ? 'rotate-180' : ''}`} />
              </button>

              {isChiSiamoOpen && (
                <div className="absolute top-full left-0 w-56 pt-2">
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 py-2 animate-fade-in">
                    <Link to="/mission" className="block px-4 py-3 text-gray-700 hover:bg-[#EEF1FA] hover:text-[#1B2380] transition-colors duration-300" onClick={() => setIsChiSiamoOpen(false)}>
                      <span className="font-medium">La Nostra Mission</span>
                      <p className="text-xs text-gray-500 mt-1">Il cuore della nostra identità</p>
                    </Link>
                    <Link to="/societa-benefit" className="block px-4 py-3 text-gray-700 hover:bg-[#EEF1FA] hover:text-[#1B2380] transition-colors duration-300" onClick={() => setIsChiSiamoOpen(false)}>
                      <span className="font-medium">Identità Benefit</span>
                      <p className="text-xs text-gray-500 mt-1">Il bene comune come fine</p>
                    </Link>
                    <Link to="/societa-cooperativa" className="block px-4 py-3 text-gray-700 hover:bg-[#EEF1FA] hover:text-[#1B2380] transition-colors duration-300" onClick={() => setIsChiSiamoOpen(false)}>
                      <span className="font-medium">Anima Cooperativa</span>
                      <p className="text-xs text-gray-500 mt-1">La forza della mutualità</p>
                    </Link>
                    <div className="border-t border-gray-100 my-2"></div>
                    <button onClick={() => { scrollToSection('team'); setIsChiSiamoOpen(false); }} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-[#EEF1FA] hover:text-[#0E8C84] transition-colors duration-300">
                      <span className="font-medium">Il Nostro Team</span>
                      <p className="text-xs text-gray-500 mt-1">Le persone dietro Assietica</p>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button data-testid="nav-servizi" onClick={() => scrollToSection('benefici')} className={navLink}>
              Servizi
            </button>
            <button data-testid="nav-societa-benefit" onClick={() => scrollToSection('societa-benefit')} className={navLink}>
              Società Benefit
            </button>
            <button
              data-testid="nav-contattaci"
              onClick={() => scrollToSection('contatti')}
              className="bg-[#0E8C84] hover:bg-[#0B7770] text-white px-6 py-2.5 rounded-lg font-semibold transition-colors duration-300"
            >
              Contattaci
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-[#1B2380] transition-colors duration-300"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden pb-6 animate-fade-in bg-white">
            <nav className="flex flex-col space-y-2">
              <div className="border-b border-gray-100 pb-2">
                <button
                  data-testid="mobile-nav-chi-siamo"
                  onClick={() => setIsMobileChiSiamoOpen(!isMobileChiSiamoOpen)}
                  className="flex items-center justify-between w-full text-left text-[#1B2380] font-medium py-2 transition-colors duration-300"
                >
                  <span>Chi Siamo</span>
                  <ChevronDown size={16} className={`transition-transform duration-300 ${isMobileChiSiamoOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileChiSiamoOpen && (
                  <div className="pl-4 space-y-2 mt-2">
                    <Link data-testid="mobile-nav-mission" to="/mission" className="block text-gray-600 hover:text-[#0E8C84] py-2" onClick={() => setIsMobileMenuOpen(false)}>→ La Nostra Mission</Link>
                    <Link data-testid="mobile-nav-benefit" to="/societa-benefit" className="block text-gray-600 hover:text-[#0E8C84] py-2" onClick={() => setIsMobileMenuOpen(false)}>→ Identità Benefit</Link>
                    <Link data-testid="mobile-nav-cooperativa" to="/societa-cooperativa" className="block text-gray-600 hover:text-[#0E8C84] py-2" onClick={() => setIsMobileMenuOpen(false)}>→ Anima Cooperativa</Link>
                    <button data-testid="mobile-nav-team" onClick={() => scrollToSection('team')} className="block text-gray-600 hover:text-[#0E8C84] py-2">→ Il Nostro Team</button>
                  </div>
                )}
              </div>

              <button onClick={() => scrollToSection('benefici')} className="text-left text-[#1B2380] font-medium py-2">Servizi</button>
              <button onClick={() => scrollToSection('societa-benefit')} className="text-left text-[#1B2380] font-medium py-2">Società Benefit</button>
              <button onClick={() => scrollToSection('contatti')} className="bg-[#0E8C84] hover:bg-[#0B7770] text-white px-6 py-3 rounded-lg font-semibold text-left mt-2 transition-colors">
                Contattaci
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
